/**
 * Статистика tool-вызовов чат-агента (chat-tools.jsonl): что агент дёргает,
 * как часто, сколько это занимает и сколько байт тащит в контекст.
 * Сырьё для оптимизации промпта/тулов/модели: тяжёлые по байтам тулы
 * раздувают контекст (= расходы), частые повторы — кандидат на обогащение
 * ответа, ошибки — на починку.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Alert, Badge, Card, Code, Group, Loader, SegmentedControl, Stack, Table,
    Text, Title, Tooltip,
} from '@mantine/core';
import { fetchToolUsage } from '../../lib/api-admin';

function fmtBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export function AdminToolsPage(): JSX.Element {
    const [days, setDays] = useState('7');
    const q = useQuery({
        queryKey: ['admin-tool-usage', days],
        queryFn:  () => fetchToolUsage(Number(days)),
        retry:    false,
    });

    return (
        <Stack gap="md">
            <Group justify="space-between" wrap="wrap">
                <Title order={2}>Тулы чат-агента</Title>
                <SegmentedControl
                    size="xs"
                    value={days}
                    onChange={setDays}
                    data={[
                        { value: '1',   label: 'Сутки' },
                        { value: '7',   label: '7 дней' },
                        { value: '30',  label: '30 дней' },
                        { value: '365', label: 'Всё' },
                    ]}
                />
            </Group>

            {q.isLoading && <Loader />}
            {q.error && <Alert color="red">{(q.error as Error).message}</Alert>}

            {q.data && (
                <>
                    <Group gap="md" wrap="wrap">
                        <StatCard label="Вызовов" value={String(q.data.totals.calls)} />
                        <StatCard label="Сессий" value={String(q.data.totals.sessions)} />
                        <StatCard label="Ошибок" value={String(q.data.totals.errors)}
                            color={q.data.totals.errors > 0 ? 'red' : undefined} />
                        <StatCard label="В контекст" value={fmtBytes(q.data.totals.totalBytes)}
                            hint="суммарный размер ответов тулов — раздувает промпт и расходы" />
                    </Group>

                    {q.data.tools.length === 0 ? (
                        <Alert color="gray" title="Данных пока нет">
                            Лог начал собираться с деплоя логирования — прогоните диалог в чате.
                        </Alert>
                    ) : (
                        <Card withBorder padding="md">
                            <Table fz="sm" striped highlightOnHover>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Тул</Table.Th>
                                        <Table.Th ta="right">Вызовов</Table.Th>
                                        <Table.Th ta="right">Ошибок</Table.Th>
                                        <Table.Th ta="right">p50, мс</Table.Th>
                                        <Table.Th ta="right">max, мс</Table.Th>
                                        <Table.Th ta="right">Ответ, avg</Table.Th>
                                        <Table.Th ta="right">Ответ, всего</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {q.data.tools.map(t => (
                                        <Table.Tr key={t.tool}>
                                            <Table.Td><Code>{t.tool}</Code></Table.Td>
                                            <Table.Td ta="right">{t.calls}</Table.Td>
                                            <Table.Td ta="right">
                                                {t.errors > 0
                                                    ? <Badge size="xs" color="red" variant="light">{t.errors}</Badge>
                                                    : '—'}
                                            </Table.Td>
                                            <Table.Td ta="right">{t.p50Ms}</Table.Td>
                                            <Table.Td ta="right">{t.maxMs}</Table.Td>
                                            <Table.Td ta="right">{fmtBytes(t.avgBytes)}</Table.Td>
                                            <Table.Td ta="right">{fmtBytes(t.totalBytes)}</Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        </Card>
                    )}

                    {q.data.rows.length > 0 && (
                        <Card withBorder padding="md">
                            <Title order={5} mb="sm">Последние вызовы</Title>
                            <Table fz="xs" striped>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Время</Table.Th>
                                        <Table.Th>Тул</Table.Th>
                                        <Table.Th>Аргументы</Table.Th>
                                        <Table.Th ta="right">мс</Table.Th>
                                        <Table.Th ta="right">Ответ</Table.Th>
                                        <Table.Th>Кто</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {q.data.rows.slice(0, 40).map((r, i) => (
                                        <Table.Tr key={i} style={r.ok ? undefined : { background: 'var(--mantine-color-red-light)' }}>
                                            <Table.Td style={{ whiteSpace: 'nowrap' }}>{r.ts.slice(5, 19).replace('T', ' ')}</Table.Td>
                                            <Table.Td><Code>{r.tool}</Code></Table.Td>
                                            <Table.Td style={{ maxWidth: 320 }}>
                                                <Text fz="xs" ff="monospace" truncate title={r.input}>{r.input}</Text>
                                            </Table.Td>
                                            <Table.Td ta="right">{r.durationMs ?? '—'}</Table.Td>
                                            <Table.Td ta="right">{fmtBytes(r.outputBytes)}</Table.Td>
                                            <Table.Td style={{ whiteSpace: 'nowrap' }}>
                                                {r.user.split('@')[0]} · {r.owner}/{r.repo}
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        </Card>
                    )}
                </>
            )}
        </Stack>
    );
}

function StatCard({ label, value, hint, color }: {
    label: string; value: string; hint?: string; color?: string;
}): JSX.Element {
    const card = (
        <Card withBorder padding="sm" style={{ minWidth: 130 }}>
            <Text fz="xs" c="dimmed">{label}</Text>
            <Text fz="xl" fw={700} c={color}>{value}</Text>
        </Card>
    );
    return hint ? <Tooltip label={hint} withArrow>{card}</Tooltip> : card;
}
